import { api } from './client';
import type { TransactionDetail, TransactionListPage, TransactionStatus, Uuid } from '../types/api';

export interface ListTransactionsParams {
  status?: TransactionStatus;
  filter?: 'active' | 'completed' | 'cancelled' | 'declined' | 'all';
  scope?: 'client' | 'associate';
  page?: number;
  pageSize?: number;
  q?: string;
}

export async function listTransactions(
  params: ListTransactionsParams = {},
): Promise<TransactionListPage> {
  const { data } = await api.get<TransactionListPage>('/transactions', { params });
  return data;
}

export async function getTransaction(id: Uuid): Promise<TransactionDetail> {
  const { data } = await api.get<TransactionDetail>(`/transactions/${id}`);
  return data;
}

export interface CreateTransactionPayload {
  serviceId: Uuid;
  subServiceId?: Uuid;
  questionnaireAnswers?: Record<string, unknown>;
  tncAccepted: true;
}

export async function createTransaction(
  payload: CreateTransactionPayload,
): Promise<TransactionDetail> {
  const { data } = await api.post<TransactionDetail>('/transactions', payload);
  return data;
}

export async function cancelTransaction(id: Uuid, reason: string): Promise<TransactionDetail> {
  const { data } = await api.post<TransactionDetail>(`/transactions/${id}/cancel`, { reason });
  return data;
}

export async function approveTransaction(id: Uuid): Promise<TransactionDetail> {
  const { data } = await api.post<TransactionDetail>(`/transactions/${id}/approve`);
  return data;
}

export async function sendForApproval(id: Uuid): Promise<TransactionDetail> {
  const { data } = await api.post<TransactionDetail>(`/transactions/${id}/send-for-approval`);
  return data;
}

export async function requestRevision(id: Uuid, notes: string): Promise<TransactionDetail> {
  const { data } = await api.post<TransactionDetail>(`/transactions/${id}/request-revision`, {
    notes,
  });
  return data;
}

export async function completeTransaction(
  id: Uuid,
  payload?: { rating?: number; feedbackDescription?: string },
): Promise<TransactionDetail> {
  const { data } = await api.post<TransactionDetail>(`/transactions/${id}/complete`, payload);
  return data;
}

export async function updateNickName(
  id: Uuid,
  nickName: string | null,
): Promise<TransactionDetail> {
  const { data } = await api.patch<TransactionDetail>(`/transactions/${id}/nickname`, { nickName });
  return data;
}

export async function updateNarration(
  id: Uuid,
  narration: string | null,
): Promise<TransactionDetail> {
  const { data } = await api.patch<TransactionDetail>(`/transactions/${id}/narration`, {
    narration,
  });
  return data;
}

export async function acceptAssignment(id: Uuid): Promise<TransactionDetail> {
  const { data } = await api.post<TransactionDetail>(`/transactions/${id}/accept-assignment`);
  return data;
}

export async function declineAssignment(id: Uuid): Promise<TransactionDetail> {
  const { data } = await api.post<TransactionDetail>(`/transactions/${id}/decline-assignment`);
  return data;
}

export async function getAssociatePublicProfile(id: Uuid) {
  const { data } = await api.get<import('../types/api').AssociatePublicProfileResponse>(
    `/transactions/${id}/associate-profile`,
  );
  return data;
}

export async function verifyAssociate(
  id: Uuid,
  payload: { action: 'CONTINUE' | 'REJECT'; reason?: string },
): Promise<TransactionDetail> {
  const { data } = await api.post<TransactionDetail>(
    `/transactions/${id}/verify-associate`,
    payload,
  );
  return data;
}

export async function raiseGrievance(id: Uuid, reason: string): Promise<TransactionDetail> {
  const { data } = await api.post<TransactionDetail>(`/transactions/${id}/raise-grievance`, {
    reason,
  });
  return data;
}

export async function updateRequiredDocuments(
  id: Uuid,
  requiredDocuments: string[],
): Promise<TransactionDetail> {
  const { data } = await api.patch<TransactionDetail>(`/transactions/${id}/required-documents`, {
    requiredDocuments,
  });
  return data;
}
